import { z } from 'zod';
import { isoDateTimeSchema, pageQuerySchema, uuidSchema } from './common.js';
import { confidenceSchema, extractedDocumentTypeSchema, extractionIssueSchema, } from './extraction.js';
/**
 * The human review contract.
 *
 * The AI proposes, the human approves, and every correction is kept. Nothing
 * here posts, categorises, or learns — this phase's only job is to capture
 * high-quality human judgement and record it permanently.
 */
export const REVIEW_STATUSES = [
    'open',
    'in_progress',
    'resolved',
    'dismissed',
    'escalated',
];
export const reviewStatusSchema = z.enum(REVIEW_STATUSES);
/**
 * The five states a reviewer thinks in, mapped onto the enum Phase 2 shipped.
 *
 * "Draft" is not a status — it is the presence of unsaved work on an item that
 * is in progress. Adding a sixth enum value for it would have made two columns
 * responsible for one fact.
 */
export const REVIEW_STATUS_LABELS = {
    open: 'To review',
    in_progress: 'In progress',
    resolved: 'Completed',
    dismissed: 'Rejected',
    escalated: 'Needs a second look',
};
export const REVIEW_OUTCOMES = ['approved', 'corrected', 'rejected', 'deferred'];
export const reviewOutcomeSchema = z.enum(REVIEW_OUTCOMES);
export const REVIEW_REASONS = [
    'low_confidence',
    'missing_required_field',
    'rule_conflict',
    'amount_threshold',
    'duplicate_suspected',
    'tax_ambiguous',
    'new_vendor',
    'period_closed',
    'manual_flag',
];
export const reviewReasonSchema = z.enum(REVIEW_REASONS);
/** Why this landed in the queue, in the reviewer's language. */
export const REVIEW_REASON_LABELS = {
    low_confidence: 'Read with low confidence',
    missing_required_field: 'Something required is missing',
    rule_conflict: 'Conflicts with a rule',
    amount_threshold: 'Large amount',
    duplicate_suspected: 'Possible duplicate',
    tax_ambiguous: 'Tax treatment unclear',
    new_vendor: 'Supplier not seen before',
    period_closed: 'Period is closed',
    manual_flag: 'Flagged for attention',
};
/* -------------------------------------------------------------------------- */
/* Field states                                                               */
/* -------------------------------------------------------------------------- */
/**
 * What a reviewer needs to know about one field at a glance.
 *
 * Derived here, in the contract, rather than in the web app: the API sorts and
 * counts by these states, and two implementations of the same rule is how a
 * badge ends up disagreeing with the number beside it.
 */
export const FIELD_STATES = [
    'edited', // the reviewer changed it — outranks everything else
    'conflict', // another reviewer changed it while this one was reading
    'invalid', // a validation error names this field
    'missing', // required, and neither the model nor the reviewer supplied it
    'warning', // a validation warning names this field
    'unsure', // below the confidence bar
    'accepted', // read confidently, nothing wrong with it
    'empty', // absent from the document, and not required
];
export const fieldStateSchema = z.enum(FIELD_STATES);
/** The fields nothing can be filed without. */
export const REQUIRED_FIELD_PATHS = [
    'vendor.name',
    'dates.transactionDate',
    'totals.total',
];
/** Below this a value is shown as needing a second look. */
export const REVIEW_CONFIDENCE_BAR = 0.85;
/**
 * Resolved in priority order, most actionable first.
 *
 * A field that is both edited and low-confidence is *edited* — the reviewer has
 * already dealt with it, and continuing to flag it would train them to ignore
 * the flag.
 */
export function resolveFieldState(input) {
    if (input.hasConflict)
        return 'conflict';
    if (input.wasEdited)
        return 'edited';
    const forThisField = input.issues.filter((issue) => issue.fieldPath === input.fieldPath);
    if (forThisField.some((issue) => issue.severity === 'error'))
        return 'invalid';
    const isRequired = REQUIRED_FIELD_PATHS.includes(input.fieldPath);
    if (input.value === null || input.value === undefined || input.value === '') {
        return isRequired ? 'missing' : 'empty';
    }
    if (forThisField.some((issue) => issue.severity === 'warning'))
        return 'warning';
    if (input.confidence < REVIEW_CONFIDENCE_BAR)
        return 'unsure';
    return 'accepted';
}
/** States that must be dealt with before a document can be approved. */
export const BLOCKING_FIELD_STATES = ['missing', 'invalid', 'conflict'];
export function isBlocking(state) {
    return BLOCKING_FIELD_STATES.includes(state);
}
/* -------------------------------------------------------------------------- */
/* Queue                                                                      */
/* -------------------------------------------------------------------------- */
export const REVIEW_SORTS = [
    'priority',
    'newest',
    'oldest',
    'confidence',
    'vendor',
    'documentType',
    'status',
];
export const reviewSortSchema = z.enum(REVIEW_SORTS);
export const listReviewQueueQuerySchema = pageQuerySchema.extend({
    status: reviewStatusSchema.optional(),
    reason: reviewReasonSchema.optional(),
    sort: reviewSortSchema.default('priority'),
    /** Matches filename, supplier, invoice number, hash, or uploader. */
    query: z.string().trim().min(1).max(200).optional(),
    assignedToMe: z.coerce.boolean().optional(),
    documentType: extractedDocumentTypeSchema.optional(),
});
export const reviewQueueItemSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    documentId: uuidSchema.nullable(),
    status: reviewStatusSchema,
    reason: reviewReasonSchema,
    priority: z.number().int(),
    title: z.string(),
    detail: z.string().nullable(),
    version: z.number().int(),
    /** Denormalised for the list, so a hundred rows are one query. */
    vendorName: z.string().nullable(),
    documentType: extractedDocumentTypeSchema.nullable(),
    total: z.string().nullable(),
    transactionDate: z.string().nullable(),
    meanConfidence: confidenceSchema.nullable(),
    errorCount: z.number().int(),
    warningCount: z.number().int(),
    hasPreview: z.boolean(),
    assignedTo: z
        .object({ userId: uuidSchema.nullable(), fullName: z.string().nullable() })
        .nullable(),
    hasDraft: z.boolean(),
    createdAt: isoDateTimeSchema,
    resolvedAt: isoDateTimeSchema.nullable(),
});
/* -------------------------------------------------------------------------- */
/* One item, open for review                                                  */
/* -------------------------------------------------------------------------- */
export const reviewFieldSchema = z.object({
    fieldPath: z.string(),
    label: z.string(),
    /** What the model read. Never mutated — the record of what it proposed. */
    predictedValue: z.unknown(),
    /** What the reviewer typed, if they touched it. */
    draftValue: z.unknown().optional(),
    confidence: confidenceSchema,
    state: fieldStateSchema,
    issues: z.array(extractionIssueSchema),
    /** Which extraction run produced it, for the source badge. */
    predictionId: uuidSchema.nullable(),
});
export const reviewNoteSchema = z.object({
    id: uuidSchema,
    body: z.string(),
    authorName: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
export const reviewItemDetailSchema = reviewQueueItemSchema.extend({
    extractionId: uuidSchema.nullable(),
    promptVersion: z.number().int().nullable(),
    modelName: z.string().nullable(),
    fields: z.array(reviewFieldSchema),
    issues: z.array(extractionIssueSchema),
    notes: z.array(reviewNoteSchema),
    draft: z
        .object({
        version: z.number().int(),
        startedAt: isoDateTimeSchema,
        updatedAt: isoDateTimeSchema,
        updatedByName: z.string().nullable(),
    })
        .nullable(),
    /** The next item in the reviewer's current ordering, for J/K navigation. */
    nextItemId: uuidSchema.nullable(),
    previousItemId: uuidSchema.nullable(),
});
/* -------------------------------------------------------------------------- */
/* Writing                                                                    */
/* -------------------------------------------------------------------------- */
export const saveDraftRequestSchema = z.object({
    /** Only what the reviewer touched. */
    fieldValues: z.record(z.string(), z.unknown()),
    /**
     * The version the reviewer loaded. A mismatch means someone else edited it
     * meanwhile, and the save is refused rather than overwriting them.
     */
    version: z.number().int().min(1),
});
export const correctionSchema = z.object({
    fieldPath: z.string().min(1).max(200),
    correctedValue: z.unknown(),
    /**
     * Optional, and worth asking for: in Phase 7 "misread a digit" and "the
     * supplier renamed" are different lessons, and only a human can tell them
     * apart at the moment of correction.
     */
    reason: z.string().trim().min(3).max(500).optional(),
});
export const submitReviewRequestSchema = z.object({
    outcome: z.enum(['approved', 'rejected', 'escalated']),
    version: z.number().int().min(1),
    corrections: z.array(correctionSchema).max(500).default([]),
    /** Document-level note, distinct from a per-field reason. */
    notes: z.string().trim().min(1).max(2000).optional(),
});
export const addNoteRequestSchema = z.object({
    body: z.string().trim().min(1).max(10_000),
});
/* -------------------------------------------------------------------------- */
/* Observability                                                              */
/* -------------------------------------------------------------------------- */
export const reviewStatsSchema = z.object({
    open: z.number().int(),
    inProgress: z.number().int(),
    resolvedToday: z.number().int(),
    /** Median rather than mean: one interrupted review skews an average badly. */
    medianSecondsToDecide: z.number().nullable(),
    approvalRate: z.number().nullable(),
    rejectionRate: z.number().nullable(),
    /** Share of reviewed documents where at least one field was corrected. */
    correctionRate: z.number().nullable(),
    averageConfidence: z.number().nullable(),
    /** Where the model is weakest — the first thing to fix in a prompt. */
    mostCorrectedFields: z.array(z.object({ fieldPath: z.string(), label: z.string(), corrections: z.number().int() })),
    reviewerThroughput: z.array(z.object({
        reviewerName: z.string().nullable(),
        reviewed: z.number().int(),
        medianSecondsToDecide: z.number().nullable(),
    })),
});
/* -------------------------------------------------------------------------- */
/* Keyboard                                                                   */
/* -------------------------------------------------------------------------- */
/**
 * The shortcut map, defined once.
 *
 * Kept in the contract so the help overlay and the handler cannot drift — a
 * help screen that lies about a shortcut is worse than no help screen.
 */
export const REVIEW_SHORTCUTS = [
    { keys: 'A', action: 'approve', description: 'Approve and go to the next document' },
    { keys: 'R', action: 'reject', description: 'Reject this extraction' },
    { keys: 'S', action: 'save', description: 'Save a draft and stay' },
    { keys: 'E', action: 'edit', description: 'Edit the focused field' },
    { keys: 'Tab', action: 'nextField', description: 'Next field' },
    { keys: 'Shift+Tab', action: 'previousField', description: 'Previous field' },
    { keys: 'J', action: 'nextDocument', description: 'Next document' },
    { keys: 'K', action: 'previousDocument', description: 'Previous document' },
    { keys: 'Esc', action: 'cancel', description: 'Cancel editing' },
    { keys: '?', action: 'help', description: 'Show this list' },
];
/** Waiting documents from one supplier this business has not decided about yet. */
export const supplierToTeachSchema = z.object({
    groupKey: z.string(),
    names: z.array(z.string()),
    documentCount: z.number().int(),
    totalMinor: z.string(),
    suggestedVendorId: z.string().nullable(),
    suggestedVendorName: z.string().nullable(),
    suggestedCategoryId: z.string().nullable(),
});
/** The answer, once: an existing supplier or a new one, and its account. */
export const teachSupplierRequestSchema = z
    .object({
    groupKey: z.string().min(1).max(300),
    names: z.array(z.string().min(1).max(300)).min(1).max(100),
    vendorId: z.uuid().optional(),
    newVendorName: z.string().trim().min(1).max(200).optional(),
    categoryId: z.uuid(),
})
    .refine((value) => (value.vendorId === undefined) !== (value.newVendorName === undefined), {
    message: 'Choose a supplier on file, or name a new one.',
});
export const teachSupplierResultSchema = z.object({
    vendorId: z.string(),
    filed: z.number().int(),
});
//# sourceMappingURL=review.js.map